import axios from "axios";
import { ADDRESS_TYPES } from "utils/CONSTANT";

export const ProvinceListKey = "ProvinceList";
export const DistrictListKey = "DistrictList";
export const SubDistrictListKey = "SubDistrictList";
export const BankListKey = "BankList";
export const PostalCodeListKey = "PostalCodeList";
export const ProvinceVersionKey = "ProvinceVersion";
export const DistrictVersionKey = "DistrictVersion";
export const SubDistrictVersionKey = "SubDistrictVersion";
export const BankVersionKey = "BankVersion";

// Compare address version from server with our localStorage
const compareAddressVersion = (
  provinceVersion,
  districtVersion,
  subDistrictVersion
) => {
  let provinceVersionLocal = localStorage.getItem(ProvinceVersionKey);
  let districtVersionLocal = localStorage.getItem(DistrictVersionKey);
  let subDistrictVersionLocal = localStorage.getItem(SubDistrictVersionKey);
  if (
    provinceVersionLocal === provinceVersion &&
    districtVersionLocal === districtVersion &&
    subDistrictVersionLocal === subDistrictVersion
  ) {
    return true;
  } else {
    return false;
  }
};

const state = {
  completeSyncAddress: false,
  provinceList: [],
  districtList: [],
  subDistrictList: [],
  postalCodeList: [],
  bankList: [],
};

const getters = {};

const actions = {
  async getLocalVersions({ commit }) {
    let provinceVersionLocal = localStorage.getItem(ProvinceVersionKey);
    let districtVersionLocal = localStorage.getItem(DistrictVersionKey);
    let subDistrictVersionLocal = localStorage.getItem(SubDistrictVersionKey);
    let bankNameVersionLocal = localStorage.getItem(BankVersionKey);

    return {
      provinceVersion: provinceVersionLocal,
      districtVersion: districtVersionLocal,
      subDistrictVersion: subDistrictVersionLocal,
      bankNameVersion: bankNameVersionLocal,
    };
  },

  async getAddressVersions({ commit }) {
    try {
      const response = await axios.get("/v1/system/version");
      return response.data.items;
    } catch (error) {
      // if we have error when get address version, we will set null to localStorage
      return null;
    }
  },

  async getProvinces({ commit }) {
    try {
      const { data } = await axios.get("/v1/province/list");
      localStorage.setItem(ProvinceListKey, JSON.stringify(data.items));
      commit("setProvinces", data);
      return data;
    } catch (error) {
      return [];
    }
  },

  async getDistricts({ commit }, provinceId) {
    try {
      const { data } = await axios.get(`/v1/district/list`);
      localStorage.setItem(DistrictListKey, JSON.stringify(data.items));
      commit("setDistricts", data.items);
      return data;
    } catch (error) {
      return [];
    }
  },

  async getSubDistricts({ commit }) {
    try {
      const { data } = await axios.get(`/v1/sub-district/list`);
      localStorage.setItem(SubDistrictListKey, JSON.stringify(data.items));
      commit("setSubDistricts", data.items);
      return data;
    } catch (error) {
      return [];
    }
  },

  async getBanks({ commit }) {
    try {
      const { data } = await axios.get("/v1/bank-info/list");
      localStorage.setItem(BankListKey, JSON.stringify(data.items));
      commit("setBanks", data.items);
      return data;
    } catch (error) {
      return [];
    }
  },
  async getBusinessUnit({ commit }) {
    try {
      const { data } = await axios.get("/v1/business-unit/list");
      if(data.items.length > 0)
      localStorage.setItem("BusinessUnit", JSON.stringify(data.items));
    } catch (error) {
      return [];
    }
  },
  getAddressInLocalStorage({ commit }) {
    const provinceList = localStorage.getItem(ProvinceListKey);
    const districtList = localStorage.getItem(DistrictListKey);
    const subDistrictList = localStorage.getItem(SubDistrictListKey);
    const bankList = localStorage.getItem(BankListKey);

    if (provinceList) {
      commit("setProvinces", JSON.parse(provinceList));
    }

    if (districtList) {
      commit("setDistricts", JSON.parse(districtList));
    }

    if (subDistrictList) {
      commit("setSubDistricts", JSON.parse(subDistrictList));
    }
    if (bankList) {
      commit("setBanks", JSON.parse(bankList));
    }
  },

  // get address version from server and compare with localStorage
  // if version is not same, we will get address from server
  async syncAddress({ commit, dispatch }) {
    commit("setCompleteSyncAddress", true);
    const {
      provinceVersion,
      districtVersion,
      subDistrictVersion,
      bankNameVersion,
    } = await dispatch("getAddressVersions");
    const {
      provinceVersion: provinceVersionLocal,
      districtVersion: districtVersionLocal,
      subDistrictVersion: subDistrictVersionLocal,
      bankNameVersion: bankNameVersionLocal,
    } = await dispatch("getLocalVersions");

    const promises = [];
    let addressChanged = "";
    const localProvinceList = localStorage.getItem(ProvinceListKey);
    const localSubDistrictList = localStorage.getItem(SubDistrictListKey);
    const localBankList = localStorage.getItem(BankListKey);
    const localDistrictList = localStorage.getItem(DistrictListKey);
    if (
      provinceVersion !== Number(provinceVersionLocal) ||
      !Array.isArray(localProvinceList) ||
      !localProvinceList.length
    ) {
      promises.push(dispatch("getProvinces"));
      dispatch("refreshAddressServerCache");
      addressChanged = ADDRESS_TYPES.PROVINCE;
    }

    if (
      districtVersion !== Number(districtVersionLocal) ||
      !Array.isArray(localDistrictList) ||
      !localDistrictList.length
    ) {
      promises.push(dispatch("getDistricts", provinceVersion));
      dispatch("refreshAddressServerCache");
      addressChanged = ADDRESS_TYPES.DISTRICT;
    }

    if (
      subDistrictVersion !== Number(subDistrictVersionLocal) ||
      !Array.isArray(localSubDistrictList) ||
      !localSubDistrictList.length
    ) {
      promises.push(dispatch("getSubDistricts", districtVersion));
      dispatch("refreshAddressServerCache");
      addressChanged = ADDRESS_TYPES.SUBDISTRICT;
    }
    if (
      bankNameVersion !== Number(bankNameVersionLocal) ||
      !Array.isArray(localBankList) ||
      !localBankList.length
    ) {
      promises.push(dispatch("getBanks"));
    }
    promises.push(dispatch("getBusinessUnit"));
    window.dispatchEvent(new Event("storage"));
    if (promises.length > 0) {
      try {
        await Promise.all(promises);
        localStorage.setItem(ProvinceVersionKey, provinceVersion);
        localStorage.setItem(DistrictVersionKey, districtVersion);
        localStorage.setItem(SubDistrictVersionKey, subDistrictVersion);
        localStorage.setItem(BankVersionKey, bankNameVersion);
      } catch (error) {
        console.log(error);
        commit("setCompleteSyncAddress", false);
      }
    }
  },

  async refreshAddressServerCache({ commit }) {
    try {
      const { data } = await axios.get(`/v1/system/reload/helper-service`);
      return data;
    } catch (error) {
      return [];
    }
  },
};

const mutations = {
  setProvinces(state, data) {
    state.provinceList = data;
  },
  setDistricts(state, data) {
    state.districtList = data;
  },
  setSubDistricts(state, data) {
    state.subDistrictList = data;
  },
  setBanks(state, data) {
    state.bankList = data;
  },
  setCompleteSyncAddress(state, data) {
    state.completeSyncAddress = data;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/system.js